import { ReactElement } from 'react'
import { Box, Checkbox } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { Html } from '@react-three/drei'

interface Props {
  x: number
  y: number
  z: number
  /** Checked while the support takes part in the edit. */
  isChecked: boolean
  onToggle: () => void
  dataCy: string
}

/**
 * The checkbox of one support, drawn at its position in the model. Rendered into
 * the scene through the control store, so it lives outside the section's form:
 * everything it needs arrives through its props.
 *
 * The drei `<Html>` portal renders outside the r3f tree, so the app's MUI theme
 * context does not reach it — we re-provide it from the theme available here
 * (same as SceneHud does).
 */
const SupportSceneCheckbox = ({ x, y, z, isChecked, onToggle, dataCy }: Props): ReactElement => {
  const theme = useTheme()

  return (
    <Html position={[x, y, z]} center zIndexRange={[1, 1]} style={{ pointerEvents: 'auto' }}>
      <ThemeProvider theme={theme}>
        {/* r3f raycasts pointer events from an ancestor of the <Html> portal, so
            an unstopped click on the checkbox also selects whatever mesh sits
            behind it. */}
        <Box
          onPointerDown={event => event.stopPropagation()}
          onPointerUp={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          sx={{ display: 'inline-flex' }}
        >
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={onToggle}
            sx={{
              padding: 0.25,
              backgroundColor: 'background.paper',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
              '&:hover': { backgroundColor: 'background.paper' },
            }}
            data-cy={dataCy}
          />
        </Box>
      </ThemeProvider>
    </Html>
  )
}

export default SupportSceneCheckbox
